import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA, trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { WPL_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * The plan qualifier — the most important non-negotiable on this page.
 *
 * Everything the page describes is Heirloom-only, and not partially: the whole
 * organizer router is mounted behind `requireProPlanWithCode`
 * (api/routes/organizer.routes.mjs), and the assistant router behind the same
 * gate plus its own. A Blossom or Bouquet couple has no Planning Hub at all —
 * not a smaller one — so a page about planning that leaves that ambiguous is a
 * page that mis-sells.
 *
 * Three statements in order, so a reader who takes only one of them still
 * cannot come away with the wrong idea:
 *
 *   1. the Planning Hub and Éros are included with Heirloom,
 *   2. photo sharing is on every plan and the Planning Hub is NOT,
 *   3. features vary by plan — with a link to the plans.
 *
 * Named as a section (`heading-50`, its own impression) rather than a footnote,
 * because a plan qualifier that reads as fine print is a plan qualifier someone
 * can honestly say they missed.
 *
 * It is also where the page hands on to its two siblings. Both links are
 * contextual rather than decorative: body2 is about photo sharing, body3 about
 * the invitation, and this is the one section on the page that talks about what
 * a plan contains rather than what the Planning Hub does.
 */
const PlanningPlan = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPL_SECTION.PLAN, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-editorial wps-editorial-solo">
          <div className="wps-editorial-text">
            <h2 className="heading-50">{t("weddingPlanning.plan.heading")}</h2>
            <p className="body-20">{t("weddingPlanning.plan.body1")}</p>
            <p className="body-20">{t("weddingPlanning.plan.body2")}</p>
            <p className="body-20">{t("weddingPlanning.plan.body3")}</p>
          </div>
        </div>

        <p className="wps-plan-note">
          {t("weddingPlanning.plan.note")}{" "}
          <a className="wps-text-link" href={localize("/#pricing")} onClick={scrollToPlans}>
            {t("weddingPlanning.plan.noteLink")}
          </a>
        </p>

        <div className="wpl-cross-links">
          {/*
            Contextual, not decorative: body3 has just said Heirloom is where
            the RSVPs and the guest list live, and /wedding-rsvp is where those
            replies come from — the front door of the guest-management half of
            the same plan.
          */}
          <a
            className="wps-text-link"
            href={localize("/wedding-rsvp")}
            onClick={() =>
              trackCTA("wedding_planning_plan", {
                ...analyticsContext,
                ctaName: "wedding_rsvp",
              })
            }
          >
            {t("weddingPlanning.plan.rsvpLink")}
          </a>
          {/*
            Contextual too: body3 names the guest list as the other half of the
            same plan, and seating is what that list is finally FOR — the
            Planning Hub's own timeline carries a seating milestone derived from
            it (api/utils/organizerSync.mjs).
          */}
          <a
            className="wps-text-link"
            href={localize("/wedding-seating")}
            onClick={() =>
              trackCTA("wedding_planning_plan", {
                ...analyticsContext,
                ctaName: "wedding_seating",
              })
            }
          >
            {t("weddingPlanning.plan.seatingLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/wedding-photo-sharing")}
            onClick={() =>
              trackCTA("wedding_planning_plan", {
                ...analyticsContext,
                ctaName: "wedding_photo_sharing",
              })
            }
          >
            {t("weddingPlanning.plan.photoLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/digital-wedding-invitation")}
            onClick={() =>
              trackCTA("wedding_planning_plan", {
                ...analyticsContext,
                ctaName: "digital_wedding_invitation",
              })
            }
          >
            {t("weddingPlanning.plan.invitationLink")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default PlanningPlan;
